import { useState } from 'react';
import { FileText, BarChart3, MessageSquare, Lightbulb, Settings, ChevronLeft, Menu, Users, CreditCard, LogOut, Activity, Shield, Brain, Database, Landmark, Flag, Building2 } from 'lucide-react';
import { colors, TopNav } from '../shared';
import { useAuth } from '../../contexts/AuthContext';
import AdminGuard from './AdminGuard';
import PromptsTab from './tabs/PromptsTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import FeedbackTab from './tabs/FeedbackTab';
import ClarityTab from './tabs/ClarityTab';
import SettingsTab from './tabs/SettingsTab';
import UsersTab from './tabs/UsersTab';
import CardsTab from './tabs/CardsTab';
import DiagnosticsTab from './tabs/DiagnosticsTab';
import PermissionsTab from './tabs/PermissionsTab';
import IntelligenceTab from './tabs/IntelligenceTab';
import DataTab from './tabs/DataTab';
import BankingTab from './tabs/BankingTab';
import FeatureFlagsTab from './tabs/FeatureFlagsTab';
import HighgroveTab from './tabs/HighgroveTab';

const tabs = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'cards', label: 'Cards', icon: CreditCard },
  { id: 'prompts', label: 'Prompts', icon: FileText },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'highgrove', label: 'Highgrove', icon: Building2 },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare },
  { id: 'clarity', label: 'Clarity Themes', icon: Lightbulb },
  { id: 'permissions', label: 'Permissions', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'banking', label: 'Banking', icon: Landmark },
  { id: 'feature-flags', label: 'Feature Flags', icon: Flag },
  { id: 'data', label: 'Data', icon: Database },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
  { id: 'diagnostics', label: 'Diagnostics', icon: Activity },
];

const TabButton = ({ tab, active, onClick, collapsed }) => {
  const Icon = tab.icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left ${
        collapsed ? 'justify-center' : ''
      }`}
      style={{
        background: active ? colors.yellowDim : 'transparent',
        color: active ? colors.yellow : colors.textSecondary,
      }}
      title={collapsed ? tab.label : undefined}
    >
      <Icon size={20} />
      {!collapsed && <span className="font-medium">{tab.label}</span>}
    </button>
  );
};

function AdminContent({ permissions, user }) {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'users':
        return <UsersTab permissions={permissions} />;
      case 'cards':
        return <CardsTab permissions={permissions} />;
      case 'diagnostics':
        return <DiagnosticsTab permissions={permissions} />;
      case 'prompts':
        return <PromptsTab permissions={permissions} />;
      case 'analytics':
        return <AnalyticsTab permissions={permissions} />;
      case 'highgrove':
        return <HighgroveTab permissions={permissions} />;
      case 'feedback':
        return <FeedbackTab permissions={permissions} />;
      case 'clarity':
        return <ClarityTab permissions={permissions} />;
      case 'permissions':
        return <PermissionsTab />;
      case 'feature-flags':
        return <FeatureFlagsTab permissions={permissions} />;
      case 'banking':
        return <BankingTab permissions={permissions} />;
      case 'data':
        return <DataTab permissions={permissions} />;
      case 'intelligence':
        return <IntelligenceTab permissions={permissions} />;
      case 'settings':
        return <SettingsTab permissions={permissions} user={user} />;
      default:
        return <UsersTab permissions={permissions} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: colors.black }}>
      <TopNav currentPath="/admin" />
      <div className="flex-1 flex">
      {/* Sidebar */}
      <aside
        className={`flex flex-col border-r transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{ background: colors.cardBg, borderColor: colors.border }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: colors.border }}>
          {!sidebarCollapsed && (
            <a href="/" className="flex items-center gap-2">
              <span className="text-lg font-bold" style={{ color: colors.yellow }}>Admin</span>
            </a>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded-lg hover:bg-opacity-50"
            style={{ color: colors.textMuted }}
          >
            {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map(tab => {
            // Check permissions for certain tabs
            if (tab.id === 'settings' && !permissions?.manageAdmins) {
              return null;
            }
            if (tab.id === 'feature-flags' && !permissions?.manageAdmins) {
              return null;
            }
            if (tab.id === 'banking' && !permissions?.manageAdmins) {
              return null;
            }
            if ((tab.id === 'analytics' || tab.id === 'highgrove') && !permissions?.viewAnalytics) {
              return null;
            }
            if (tab.id === 'feedback' && !permissions?.viewFeedback) {
              return null;
            }
            if ((tab.id === 'prompts') && !permissions?.viewPrompts) {
              return null;
            }

            return (
              <TabButton
                key={tab.id}
                tab={tab}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                collapsed={sidebarCollapsed}
              />
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className={`p-4 border-t ${sidebarCollapsed ? 'hidden' : ''}`} style={{ borderColor: colors.border }}>
          <p className="text-xs truncate mb-3" style={{ color: colors.textMuted }}>
            {user?.email}
          </p>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{ background: colors.redDim, color: colors.red }}
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {renderTab()}
      </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminGuard>
      {({ permissions, user }) => (
        <AdminContent permissions={permissions} user={user} />
      )}
    </AdminGuard>
  );
}
